/**
 * RTWO Cryptography & Identity Engine — Key Rotation Service
 */

import { CRYPTO_SERVICE_IDS } from '../domain-models';
import type { KeyPairId } from '../domain-models';
import type {
  IKeyRotationService,
  KeyRotationRequest,
  KeyRotationResult,
} from '../service-interfaces';
import type { CryptoRepositories } from '../repositories/crypto-repositories';
import type { KeyProviderRegistry } from '../registry/provider-registries';
import { CryptoEventTypes } from '../events/crypto-event-bus';
import { BaseCryptoService, type CryptoServiceContext } from './base-crypto-service';

export class KeyRotationService extends BaseCryptoService implements IKeyRotationService {
  readonly serviceId = CRYPTO_SERVICE_IDS.KEY_ROTATION;
  readonly serviceName = 'Key Rotation Service';
  readonly description = 'Manages key rotation lifecycle via Key Provider.';
  readonly layer = 'R6' as const;
  readonly integrations = ['R6 Security & Automation', 'Key Provider'];
  readonly requiresKeyProvider = true;
  readonly requiresIdentityBackend = false;

  constructor(
    context: CryptoServiceContext,
    private readonly repositories: CryptoRepositories,
    private readonly keyProviderRegistry: KeyProviderRegistry
  ) {
    super(context);
  }

  async initiateKeyRotation(request: KeyRotationRequest): Promise<KeyRotationResult> {
    const keyPair = this.repositories.keyPairs.findById(request.keyPairId);
    if (!keyPair || !this.keyProviderRegistry.isConfigured()) {
      return {
        success: false,
        oldKeyPairId: null,
        newKeyPairId: null,
        error: 'Key Provider not configured. Key rotation not available.',
        isPlaceholder: true,
        source: 'Key Provider — Not Configured',
      };
    }

    this.repositories.keyPairs.save({ ...keyPair, status: 'rotating' });
    this.context.eventBus.emit(CryptoEventTypes.KEY_ROTATION_STARTED, request.keyPairId, {
      reason: request.reason,
      requestedBy: request.requestedBy,
    });

    await this.keyProviderRegistry.getActiveProvider().rotateKey(request.keyPairId);

    return {
      success: false,
      oldKeyPairId: null,
      newKeyPairId: null,
      error:
        'Key rotation initiated but interface remains placeholder until provider certification.',
      isPlaceholder: true,
      source: 'Key Provider — Not Configured',
    };
  }

  async completeKeyRotation(keyPairId: KeyPairId): Promise<KeyRotationResult> {
    const keyPair = this.repositories.keyPairs.findById(keyPairId);
    if (!keyPair || !this.keyProviderRegistry.isConfigured()) {
      return {
        success: false,
        oldKeyPairId: null,
        newKeyPairId: null,
        error: 'Key Provider not configured. Key rotation not available.',
        isPlaceholder: true,
        source: 'Key Provider — Not Configured',
      };
    }

    const rotated = await this.keyProviderRegistry.getActiveProvider().rotateKey(keyPairId);
    if (rotated?.keyPairId) {
      this.repositories.keyPairs.save({
        ...keyPair,
        status: 'rotated',
        rotatedAt: Date.now(),
      });
      this.context.eventBus.emit(CryptoEventTypes.KEY_ROTATED, keyPairId, {
        newKeyPairId: rotated.keyPairId,
      });
    }

    return {
      success: false,
      oldKeyPairId: null,
      newKeyPairId: null,
      error:
        'Key rotation completed but interface remains placeholder until provider certification.',
      isPlaceholder: true,
      source: 'Key Provider — Not Configured',
    };
  }

  async revokeKey(keyPairId: KeyPairId, reason: string): Promise<KeyRotationResult> {
    const keyPair = this.repositories.keyPairs.findById(keyPairId);
    if (!keyPair || !this.keyProviderRegistry.isConfigured()) {
      return {
        success: false,
        oldKeyPairId: null,
        newKeyPairId: null,
        error: 'Key Provider not configured. Key revocation not available.',
        isPlaceholder: true,
        source: 'Key Provider — Not Configured',
      };
    }

    const revoked = await this.keyProviderRegistry.getActiveProvider().revokeKey(keyPairId);
    if (revoked) {
      this.repositories.keyPairs.save({ ...keyPair, status: 'revoked' });
      this.context.eventBus.emit(CryptoEventTypes.KEY_REVOKED, keyPairId, { reason });
    }

    return {
      success: false,
      oldKeyPairId: null,
      newKeyPairId: null,
      error:
        'Key revocation processed but interface remains placeholder until provider certification.',
      isPlaceholder: true,
      source: 'Key Provider — Not Configured',
    };
  }
}
